import { CallBackProps, STATUS, Step } from "react-joyride";
import { EventEmitter } from "@ismael1361/utils";

/**
 * Estado que controla o fluxo do Onboarding.
 */
export interface OnboardingState {
	run: boolean;
	steps: Step[];
	stepIndex: number;
	tutorialId: string | null;
}

const initialState: OnboardingState = {
	run: false,
	steps: [],
	stepIndex: 0,
	tutorialId: null,
};

let currentState: OnboardingState = { ...initialState };
const tutorials: Record<string, Step[]> = {};

const emitter = new EventEmitter<{
	change: [state: OnboardingState];
}>();

emitter.prepared = true;

/**
 * Serviço responsável por gerenciar os tutoriais interativos (Onboarding).
 * Controla qual tutorial está ativo, seus passos e o estado de execução.
 */
export class OnboardingServices {
	/**
	 * Retorna o estado atual do serviço de onboarding.
	 */
	static get state(): OnboardingState {
		return { ...currentState };
	}

	/**
	 * Registra um listener para notificações de mudança de estado.
	 * @param callback Função chamada quando o estado muda.
	 * @returns Função para remover o listener.
	 */
	static onChange(callback: (state: OnboardingState) => void) {
		const listener = emitter.on("change", callback);
		return () => listener.stop();
	}

	/**
	 * Define um tutorial no registro.
	 * @param id Identificador único do tutorial.
	 * @param steps Passos do tutorial (array de Step do react-joyride).
	 */
	static registerTutorial(id: string, ...steps: Step[]) {
		tutorials[id] = Array.isArray(tutorials[id]) ? tutorials[id].concat(steps) : steps;
	}

	/**
	 * Inicia a execução de um tutorial específico.
	 * @param id Identificador do tutorial a ser iniciado.
	 */
	static start(id: string, verifyExecuted: boolean = false) {
		if (!tutorials[id]) {
			console.warn(`[OnboardingServices] Tutorial '${id}' não encontrado.`);
			return;
		}

		if (verifyExecuted) {
			const executedTutorials = JSON.parse(localStorage.getItem("executedTutorials") || "[]");
			if (executedTutorials.includes(id)) {
				console.info(`[OnboardingServices] Tutorial '${id}' já foi executado anteriormente.`);
				return;
			}
		}

		currentState = {
			...currentState,
			run: true,
			steps: tutorials[id],
			stepIndex: 0,
			tutorialId: id,
		};

		if (verifyExecuted) {
			const executedTutorials = JSON.parse(localStorage.getItem("executedTutorials") || "[]");
			executedTutorials.push(id);
			localStorage.setItem("executedTutorials", JSON.stringify(executedTutorials));
		}

		emitter.emit("change", currentState);
	}

	/**
	 * Interrompe o tutorial atual.
	 */
	static stop() {
		currentState = {
			...currentState,
			run: false,
			tutorialId: null,
		};
		emitter.emit("change", currentState);
	}

	/**
	 * Método interno para processar callbacks do React Joyride.
	 * Deve ser chamado pelo componente visual que renderiza o Joyride.
	 */
	static handleJoyrideCallback(data: CallBackProps) {
		const { status, type, index } = data;
		const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

		if (finishedStatuses.includes(status)) {
			// Tutorial finalizado ou pulado
			this.stop();
		} else if (type === "step:after" || type === "error:target_not_found") {
			// Atualiza índice interno se o Joyride avançar o passo
			// Isso mantém o estado sincronizado caso o controle seja feito via props
			const nextStepIndex = index + (data.action === "prev" ? -1 : 1);

			if (currentState.run) {
				currentState = {
					...currentState,
					stepIndex: nextStepIndex,
				};
				// O emit aqui é opcional dependendo de como o componente reage,
				// mas ajuda se outros componentes precisarem saber o passo atual.
				emitter.emit("change", currentState);
			}
		}
	}
}
